// ═══════════════════════════════════════════════════════════════
// Típusbiztos API Kliens (Fetch Wrapper)
// ═══════════════════════════════════════════════════════════════

import { DEPLOYMENT } from '../config/deployment';
import type { SystemAnnouncement, SystemAnnouncementAdmin, SystemAnnouncementInput } from '../types';
import type {
  User,
  Tenant,
  LayoutManifest,
  PageItem,
  FieldItem,
  RecordItem,
  AttachmentItem,
  StagingBatch,
  SyncStatus,
  OrgUnit,
  Position,
  Person,
  AuditLogEntry,
  AdminUser,
  LegalRegulation,
  LinkedEntity,
  TemplateDefinition,
  TemplateVariableDefinition,
  OrgChartSettings,
  LinkedSettings,
  RegulationReferenceItem,
  CandidateRecordItem,
  ReplacePair,
  ReplacePreviewResult,
  SystemBrandingConfig,
  UploadPolicy,
  StorageUsage,
} from '../types';


export const API_BASE = '/api';

export interface TemplateSampleItem { id: string; pont: string; label: string; filename: string; size: number; uploaded_at: string; uploaded_by: string }

export class ApiClient {
  private static tokenKey = 'kf_token';

  static getToken(): string | null {
    return localStorage.getItem(this.tokenKey);
  }

  static setToken(token: string) {
    localStorage.setItem(this.tokenKey, token);
  }

  static clearToken() {
    localStorage.removeItem(this.tokenKey);
  }

  static getAuthFileUrl(url?: string, download = false): string {
    if (!url) return '';
    const token = this.getToken();
    let result = url;
    if (token && !result.includes('token=')) {
      const separator = result.includes('?') ? '&' : '?';
      result = `${result}${separator}token=${encodeURIComponent(token)}`;
    }
    if (download && !result.includes('download=1')) {
      const sep = result.includes('?') ? '&' : '?';
      result = `${result}${sep}download=1`;
    }
    return result;
  }

  /** melléklet tartalmának letöltése (a böngészőben feldolgozáshoz, pl. „tartalomként” betöltés) */
  static async fetchAttachmentBlob(attachmentId: number, tenantSlug?: string): Promise<Blob> {
    const token = this.getToken();
    const res = await fetch(`${API_BASE}/public/${encodeURIComponent(tenantSlug || 'system')}/attachments/${attachmentId}?download=1`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`A melléklet nem tölthető le (HTTP ${res.status})`);
    return res.blob();
  }

  static async downloadFile(url?: string, filename?: string): Promise<void> {
    if (!url) return;
    const authUrl = this.getAuthFileUrl(url, true);
    const token = this.getToken();
    try {
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(authUrl, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename || 'letoltes';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
    } catch {
      // Fallback natív letöltés / új lap megnyitás
      const a = document.createElement('a');
      a.href = authUrl;
      if (filename) a.download = filename;
      a.target = '_blank';
      a.rel = 'noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  }

  static async request<T = any>(path: string, options: RequestInit & { timeout?: number } = {}): Promise<{ ok: boolean; data?: T; error?: string; [key: string]: any }> {
    const token = this.getToken();
    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string> || {}),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    if (!(options.body instanceof FormData) && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }

    const controller = new AbortController();
    const timeoutMs = options.timeout ?? 15000;
    const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
    const signal = options.signal || controller.signal;

    try {
      const res = await fetch(`${API_BASE}${path}`, {
        ...options,
        signal,
        headers,
      });

      if (res.status === 401) {
        if (path !== '/auth/login') {
          this.clearToken();
          window.dispatchEvent(new CustomEvent('auth:expired'));
          return { ok: false, error: 'Lejárt munkamenet' };
        }
      }

      const text = await res.text();
      if (!text || text.trim() === '') {
        if (!res.ok) {
          const statusHint = res.status === 502 
            ? 'A háttérszerver (Worker API a 8787-es porton) nem érhető el! Fut a wrangler dev?' 
            : `Szerver hiba (${res.status} ${res.statusText || 'Hiba'}) - az API nem válaszol.`;
          return { ok: false, error: statusHint };
        }
        return { ok: res.ok };
      }

      let json: any;
      try {
        json = JSON.parse(text);
      } catch {
        if (!res.ok) {
          const statusHint = res.status === 502
            ? 'A háttérszerver (Worker API a 8787-es porton) nem érhető el!'
            : `Szerver hiba (${res.status}): ${text.slice(0, 150)}`;
          return { ok: false, error: statusHint };
        }
        return { ok: false, error: 'A szerver válasza nem szabványos JSON formátumú' };
      }
      // Karbantartás miatt elutasított művelet: a karbantartás-jelző azonnal megjelenjen
      if (json && json.maintenance === true && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('kozad:maintenance-changed', {
          detail: { active: true, reason: json.error || '', started_at: json.started_at || null },
        }));
      }
      return json;
    } catch (e: any) {
      if (e.name === 'AbortError') {
        console.error('API kérés időtúllépés (timeout):', path);
        return { ok: false, error: 'A szerver nem válaszolt időben (időtúllépés)' };
      }
      console.error('API hiba:', e);
      return { ok: false, error: e.message || 'Hálózati hiba történt' };
    } finally {
      clearTimeout(timeoutId);
    }
  }

  // ── AUTH ────────────────────────────────────────────────────────
  // A KÖZPONTI belépési oldal listája (csak a rendszerszervezet engedélyezett felhasználói).
  static async getDemoUsers() {
    return this.request<{ email: string; role: string; name?: string; tenant_id?: number; tenant_name?: string }[]>(`/auth/demo-users`);
  }

  // Szervezeti egyedi belépési oldal (/login/<token>): megjelenés + a szervezet engedélyezett felhasználói.
  static async getOrgLoginPage(token: string) {
    return this.request<never>(`/auth/org-login/${encodeURIComponent(token)}`) as Promise<{
      ok: boolean;
      error?: string;
      tenant?: { name: string; short_name?: string | null; logo_url?: string | null; login_subtitle?: string | null; login_bg_color?: string | null; brand_color?: string | null };
      users?: { email: string; role: string; name?: string; tenant_id?: number }[];
    }>;
  }

  // Egy szervezet egyedi belépési linkje (superadmin / a szervezet adminja).
  // ── Rendszerüzenetek (fényújság, belépési oldalak) ──
  static async getLoginAnnouncements(loginToken?: string | null) {
    const q = loginToken ? `?login_token=${encodeURIComponent(loginToken)}` : '';
    return this.request<SystemAnnouncement[]>(`/auth/login-announcements${q}`);
  }

  static async getActiveAnnouncements() {
    return this.request<SystemAnnouncement[]>('/announcements/active');
  }

  static async listAnnouncementsAdmin() {
    return this.request<SystemAnnouncementAdmin[]>('/announcements/admin');
  }

  static async saveAnnouncement(data: SystemAnnouncementInput, id?: number) {
    return this.request<SystemAnnouncementAdmin>(id ? `/announcements/${id}` : '/announcements', {
      method: id ? 'PUT' : 'POST',
      body: JSON.stringify(data),
    });
  }

  static async deleteAnnouncement(id: number) {
    return this.request(`/announcements/${id}`, { method: 'DELETE' });
  }

  static async getTenantLoginLink(tenantId: number) {
    return this.request<{ tenant_id: number; token: string; path: string }>(`/tenants/${tenantId}/login-link`);
  }

  static async regenerateTenantLoginLink(tenantId: number) {
    return this.request<{ tenant_id: number; token: string; path: string }>(`/tenants/${tenantId}/login-link/regenerate`, { method: 'POST' });
  }

  static async login(email: string, password: string, loginToken?: string) {
    const res = await this.request<{
      token?: string;
      user?: User;
      tenant?: Tenant;
      requires_2fa?: boolean;
      temp_token?: string;
      message?: string;
    }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(loginToken ? { email, password, login_token: loginToken } : { email, password }),
      timeout: 30000,
    });
    if (res.ok && res.token) {
      this.setToken(res.token);
    }
    return res;
  }

  static async verify2FALogin(tempToken: string, code: string) {
    const res = await this.request<{
      token?: string;
      user?: User;
      tenant?: Tenant;
    }>('/auth/2fa/login-verify', {
      method: 'POST',
      body: JSON.stringify({ temp_token: tempToken, code }),
    });
    if (res.ok && res.token) {
      this.setToken(res.token);
    }
    return res;
  }

  static async setup2FA() {
    return this.request<{ secret: string; uri: string }>('/auth/2fa/setup', {
      method: 'POST',
      body: '{}',
    });
  }

  static async enable2FA(secret: string, code: string) {
    return this.request<{ message: string }>('/auth/2fa/enable', {
      method: 'POST',
      body: JSON.stringify({ secret, code }),
    });
  }

  static async disable2FA(password: string) {
    return this.request<{ message: string }>('/auth/2fa/disable', {
      method: 'POST',
      body: JSON.stringify({ password }),
    });
  }

  static async forgotPassword(email: string) {
    return this.request<{ message: string; reset_link?: string }>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  static async resetPassword(token: string, newPassword: string) {
    return this.request<{ message: string }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, new_password: newPassword }),
    });
  }

  static async getProfile() {
    return this.request<{ user: User; tenant: Tenant }>('/auth/profile');
  }

  static async updateProfile(data: { name: string; email: string; notification_email?: string }) {
    return this.request<{ message: string; user: User }>('/auth/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  static async changeOwnPassword(currentPassword: string, newPassword: string) {
    return this.request<{ message: string }>('/users/me/password', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
    });
  }

  static async logout() {
    await this.request('/auth/logout', { method: 'POST', body: '{}' });
    this.clearToken();
  }

  static async me() {
    return this.request<{ user: User; tenant: Tenant }>('/auth/me');
  }

  static async impersonateTenant(tenantId: number) {
    return this.request<{ ok: boolean; token: string; user: User; tenant: Tenant }>('/auth/impersonate', {
      method: 'POST',
      body: JSON.stringify({ tenant_id: tenantId }),
    });
  }

  static async uploadLogo(file: File, field: 'logo_url' | 'login_logo_url' = 'logo_url'): Promise<{ ok: boolean; url?: string; error?: string }> {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('field', field);
    return this.request<{ url: string }>('/upload/logo', {
      method: 'POST',
      body: fd,
    });
  }

  // ── CONFIG & MANIFEST ───────────────────────────────────────────
  static async getLayout(): Promise<{ ok: boolean; data?: LayoutManifest; error?: string }> {
    return this.request<LayoutManifest>('/config/layout');
  }

  static async getLayoutOverrides(): Promise<{
    ok: boolean;
    data?: {
      globalOverrides: Record<string, any>;
      tenantOverrides: Record<string, any>;
      deletedModuleIds?: string[];
      purgedModuleIds?: string[];
    };
    error?: string;
  }> {
    return this.request('/config/layout/overrides');
  }

  static async saveLayoutOverrides(
    scope: 'global' | 'tenant',
    overrides: Record<string, any>,
    deletedModuleIds?: string[],
    purgedModuleIds?: string[]
  ): Promise<{ ok: boolean; error?: string }> {
    return this.request('/config/layout/overrides', {
      method: 'POST',
      body: JSON.stringify({ scope, overrides, deletedModuleIds, purgedModuleIds }),
    });
  }

  // ── PAGES & RECORDS ─────────────────────────────────────────────
  static async getPages(includeAll: boolean = false) {
    const qs = includeAll ? '?all=1' : '';
    return this.request<PageItem[]>(`/pages${qs}`);
  }

  static async createPage(page: Partial<PageItem>) {
    return this.request<{ id: number }>('/pages', {
      method: 'POST',
      body: JSON.stringify(page),
    });
  }

  static async updatePage(pageId: number, data: Partial<PageItem>) {
    return this.request(`/pages/${pageId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  static async deletePage(pageId: number) {
    return this.request(`/pages/${pageId}`, {
      method: 'DELETE',
    });
  }

  // ── Lomtár (szervezetenként; 30 napig visszaállítható) ──
  static async getTrash(tenantId?: number) {
    return this.request<TrashItem[]>(`/trash${tenantId ? `?tenant_id=${tenantId}` : ''}`) as Promise<{ ok: boolean; data?: TrashItem[]; days?: number; error?: string }>;
  }

  static async restoreTrashItem(id: number) {
    return this.request<never>(`/trash/${id}/restore`, { method: 'POST', timeout: 60000 }) as Promise<{ ok: boolean; error?: string; page_id?: number; record_id?: number; renamed?: string | null }>;
  }

  static async purgeTrashItem(id: number) {
    return this.request(`/trash/${id}`, { method: 'DELETE' });
  }

  static async emptyTrash(tenantId?: number) {
    return this.request<never>(`/trash${tenantId ? `?tenant_id=${tenantId}` : ''}`, { method: 'DELETE', timeout: 60000 }) as Promise<{ ok: boolean; purged?: number; error?: string }>;
  }

  // ── Archívum (szervezetenként; jogszabályi megőrzési idővel) ──
  static async getArchive(tenantId?: number) {
    return this.request<ArchiveItem[]>(`/archive${tenantId ? `?tenant_id=${tenantId}` : ''}`) as Promise<{ ok: boolean; data?: ArchiveItem[]; summary?: ArchiveSummary; auto?: boolean; today?: string; error?: string }>;
  }

  static async previewArchive(kind: ArchiveKind, ids: number[], years?: number | null) {
    return this.request<ArchivePreview>('/archive/preview', { method: 'POST', body: JSON.stringify({ kind, ids, years: years ?? null }), timeout: 60000 });
  }

  static async archiveItems(kind: ArchiveKind, ids: number[], opts: { years?: number | null; note?: string } = {}) {
    return this.request<never>('/archive', { method: 'POST', body: JSON.stringify({ kind, ids, years: opts.years ?? null, note: opts.note || null }), timeout: 120000 }) as Promise<{ ok: boolean; archived?: number; skipped?: number; error?: string }>;
  }

  static async restoreArchiveItem(id: number) {
    return this.request<never>(`/archive/${id}/restore`, { method: 'POST', timeout: 60000 }) as Promise<{ ok: boolean; error?: string; page_id?: number; record_id?: number; renamed?: string | null; kept?: boolean; as_new?: boolean }>;
  }

  static async publishArchiveItem(id: number, pub: boolean, reason?: string) {
    return this.request<never>(`/archive/${id}/publish`, { method: 'POST', body: JSON.stringify({ public: pub, reason: reason || null }) }) as Promise<{ ok: boolean; is_public?: number; error?: string }>;
  }

  static async deleteArchiveItem(id: number, reason?: string) {
    return this.request<never>(`/archive/${id}`, { method: 'DELETE', body: JSON.stringify({ reason: reason || '' }) }) as Promise<{ ok: boolean; early?: boolean; error?: string }>;
  }

  /** a teljes archívum letöltése ZIP-ben */
  static async downloadArchiveZip(tenantId?: number): Promise<{ ok: boolean; error?: string; fingerprint?: string }> {
    const token = this.getToken();
    const res = await fetch(`${API_BASE}/archive/export${tenantId ? `?tenant_id=${tenantId}` : ''}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) {
      let msg = `HTTP ${res.status}`; try { msg = (await res.json()).error || msg; } catch { /* */ }
      return { ok: false, error: msg };
    }
    const cd = res.headers.get('Content-Disposition') || '';
    const m = cd.match(/filename="([^"]+)"/);
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = m ? m[1] : 'archivum.zip';
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
    return { ok: true, fingerprint: res.headers.get('X-Archive-Fingerprint') || undefined };
  }

  static async importArchiveZip(file: File, tenantId?: number, password?: string) {
    const fd = new FormData();
    fd.append('file', file);
    if (password) fd.append('password', password);
    return this.request<never>(`/archive/import${tenantId ? `?tenant_id=${tenantId}` : ''}`, { method: 'POST', body: fd, timeout: 300000 }) as Promise<{ ok: boolean; imported?: number; skipped?: number; files?: number; missing_files?: string[]; error?: string; code?: string | null }>;
  }

  // ── Kvóta (globális alapérték + szervezeti egyedi érték) ──
  static async getQuota(tenantId?: number) {
    return this.request<QuotaStatus>(`/quota${tenantId ? `?tenant_id=${tenantId}` : ''}`);
  }

  static async requestQuota(additionalMb: number, message: string) {
    return this.request<QuotaRequest>('/quota/request', { method: 'POST', body: JSON.stringify({ additional_mb: additionalMb, message }) });
  }

  static async getAdminQuota() {
    return this.request<{ defaults: QuotaDefaults; requests: QuotaRequestRow[] }>('/admin/quota');
  }

  static async setQuotaDefaults(v: QuotaDefaults) {
    return this.request<QuotaDefaults>('/admin/quota/defaults', { method: 'PUT', body: JSON.stringify(v) });
  }

  static async decideQuotaRequest(tenantId: number, approve: boolean, opts: { max_storage_mb?: number; reason?: string }) {
    return this.request<QuotaRequest>(`/admin/quota/requests/${tenantId}/${approve ? 'approve' : 'reject'}`, { method: 'POST', body: JSON.stringify(opts) });
  }

  static async getArchiveAuto() {
    return this.request<{ all: boolean; tenants: number[] }>('/admin/archive/auto');
  }

  static async setArchiveAuto(cfg: { all: boolean; tenants: number[] }) {
    return this.request<{ all: boolean; tenants: number[] }>('/admin/archive/auto', { method: 'PUT', body: JSON.stringify(cfg) });
  }

  static async getPublicArchive(tenantSlug: string) {
    return this.request<PublicArchiveItem[]>(`/public/${encodeURIComponent(tenantSlug)}/archive`);
  }

  static async bulkDeletePages(pageIds: number[]) {
    return this.request<{ ok: boolean; deleted_count: number; message?: string }>('/pages/bulk-delete', {
      method: 'POST',
      body: JSON.stringify({ page_ids: pageIds }),
    });
  }

  static async getPageFields(pageId: number) {
    return this.request<FieldItem[]>(`/pages/${pageId}/fields`);
  }

  /** a közzétételi lista mezőinek pótlása az oldalon (a meglévő mezőket nem módosítja) */
  static async syncTemplateFields(pageId: number) {
    return this.request<never>(`/pages/${pageId}/fields/sync-template`, { method: 'POST' }) as Promise<{ ok: boolean; added?: string[]; error?: string }>;
  }

  static async createField(pageId: number, field: Partial<FieldItem>) {
    return this.request<{ id: number }>(`/pages/${pageId}/fields`, {
      method: 'POST',
      body: JSON.stringify(field),
    });
  }

  static async updateField(fieldId: number, field: Partial<FieldItem>) {
    return this.request(`/fields/${fieldId}`, {
      method: 'PUT',
      body: JSON.stringify(field),
    });
  }

  static async deleteField(fieldId: number) {
    return this.request(`/fields/${fieldId}`, {
      method: 'DELETE',
    });
  }

  static async getPageRecords(pageId: number, search?: string, year?: string | number, exactYear?: boolean, limit: number = 1000) {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (year !== undefined && year !== null && year !== '') params.append('year', String(year));
    if (exactYear) params.append('exact_year', 'true');
    params.append('limit', String(limit));
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<RecordItem[]>(`/pages/${pageId}/records${query}`);
  }

  static async saveRecord(pageId: number, data: Record<string, any>, recordId?: number) {
    if (recordId) {
      return this.request(`/records/${recordId}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
    }
    return this.request(`/pages/${pageId}/records`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  static async getRecord(recordId: number) {
    return this.request<RecordItem>(`/records/${recordId}`);
  }

  static async deleteRecord(recordId: number) {
    return this.request(`/records/${recordId}`, { method: 'DELETE' });
  }

  // ── CSATOLMÁNYOK (ATTACHMENTS) ──────────────────────────
  static async getRecordAttachments(recordId: number) {
    return this.request<AttachmentItem[]>(`/records/${recordId}/attachments`);
  }

  static async uploadRecordAttachments(recordId: number, files: File[]) {
    const formData = new FormData();
    for (const f of files) {
      formData.append('files', f);
    }
    // a válasz „variables” mezője: a DOCX / XLSX fájlokban kitöltött {{KULCS}} változók (fájlonként)
    return this.request<{ ok: boolean; uploaded?: Array<{ id: number; filename: string; url: string; variables_filled?: boolean }>; variables?: { filename: string; replaced: string[]; unresolved: string[] }[] }>(`/records/${recordId}/attachments`, {
      method: 'POST',
      body: formData,
    });
  }

  static async deleteAttachment(attachmentId: number) {
    return this.request(`/attachments/${attachmentId}`, {
      method: 'DELETE',
    });
  }

  static async renameAttachment(attachmentId: number, filename: string) {
    return this.request<{ ok: boolean; filename: string }>(`/attachments/${attachmentId}`, {
      method: 'PATCH',
      body: JSON.stringify({ filename }),
    });
  }

  static async assignAttachment(attachmentId: number, targetRecordId: number, mode: 'copy' | 'move' = 'copy') {
    return this.request<{ ok: boolean; new_id?: number; id?: number }>(`/attachments/${attachmentId}/assign`, {
      method: 'POST',
      body: JSON.stringify({ target_record_id: targetRecordId, mode }),
    });
  }

  static async uploadBulkAttachments(files: File[], mappings: { record_id: number }[]) {
    const formData = new FormData();
    for (const f of files) {
      formData.append('files', f);
    }
    formData.append('mappings', JSON.stringify(mappings));
    return this.request<{ ok: boolean; results?: Array<{ filename: string; ok: boolean; id?: number; error?: string }> }>('/attachments/bulk', {
      method: 'POST',
      body: formData,
    });
  }

  static async getAttachmentsOverview(tenantId?: number, pageId?: number) {
    const params = new URLSearchParams();
    if (tenantId) params.append('tenant_id', String(tenantId));
    if (pageId) params.append('page_id', String(pageId));
    const qs = params.toString() ? `?${params.toString()}` : '';
    return this.request<{
      tenant_id: number;
      tenant_slug: string;
      tenant_name: string;
      total_count: number;
      total_size: number;
      warning_count: number;
      types: Record<string, { label: string; count: number; size: number; ext: string; color: string }>;
      files: Array<{
        id: number;
        filename: string;
        ext: string;
        size: number;
        mime_type: string;
        uploaded_at: string;
        type_group: string;
        is_warning: boolean;
        record_id: number;
        record_pont: string;
        record_title: string;
        page_id: number;
        page_title: string;
        page_slug: string;
        page_icon: string;
        url: string;
        source_url?: string | null;
      }>;
      failed_count?: number;
      failures?: Array<{
        id: number;
        label: string;
        filename: string;
        source_url: string;
        reason: string;
        attempts: number;
        last_attempt_at: string;
        record_id: number;
        record_pont: string;
        record_title: string;
        page_id: number;
        page_title: string;
        page_slug: string;
        page_icon: string;
      }>;
    }>(`/attachments/overview${qs}`);
  }

  // Le nem töltött melléklet újrapróbálása / meglévő melléklet újra letöltése az eredeti címről.
  // Egy hívás = egy fájl; a letöltési időkorlát a superadmin beállítása (max. 300 mp).
  static async retryAttachmentFailure(failureId: number) {
    return this.request<{ ok: boolean; filename?: string; size?: number; error?: string }>(`/attachment-failures/${failureId}/retry`, { method: 'POST', timeout: 330000 });
  }

  // Fájl helyett érkezett weboldal kitisztított tartalma (mentés képként / PDF-ként / HTML-ként)
  static async getFailurePage(failureId: number) {
    return this.request<{ ok: boolean; title?: string; html?: string; source_url?: string; fetched_at?: string; error?: string }>(`/attachment-failures/${failureId}/page`, { method: 'POST', timeout: 330000 });
  }

  // Le nem töltött tétel lezárása (mentés után) vagy elvetése
  static async resolveAttachmentFailure(failureId: number, savedAs?: string) {
    return this.request(`/attachment-failures/${failureId}`, { method: 'DELETE', body: JSON.stringify(savedAs ? { saved_as: savedAs } : {}) });
  }

  static async refetchAttachment(attachmentId: number) {
    return this.request<{ ok: boolean; filename?: string; size?: number; error?: string }>(`/attachments/${attachmentId}/refetch`, { method: 'POST', timeout: 330000 });
  }

  static async replaceAttachment(attachmentId: number, file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.request<{ ok: boolean; message: string; id: number; filename: string; size: number }>(`/attachments/${attachmentId}/replace`, {
      method: 'POST',
      body: formData,
    });
  }


  static async exportPageXLSX(pageId: number): Promise<Blob> {
    const token = this.getToken();
    const res = await fetch(`${API_BASE}/pages/${pageId}/export/xlsx`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    if (!res.ok) {
      throw new Error(`XLSX export hiba (${res.status}): ${res.statusText}`);
    }
    return res.blob();
  }

  // ── STAGING (Mappastruktúra ZIP Import) ──────────────────────────
  static async uploadZipForStaging(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.request<StagingBatch>('/staging/upload', {
      method: 'POST',
      body: formData,
      timeout: 300000, // nagy ZIP feldolgozása is beleférjen
    });
  }

  static async getStagingBatch(stagingId: string) {
    return this.request<StagingBatch>(`/staging/${stagingId}`);
  }

  static async updateStagingItem(stagingId: string, itemId: string, updates: Record<string, any>) {
    return this.request(`/staging/${stagingId}/items/${itemId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  static async deleteStagingItem(stagingId: string, itemId: string) {
    return this.request(`/staging/${stagingId}/items/${itemId}`, {
      method: 'DELETE',
    });
  }

  static async addStagingItem(stagingId: string, item: { pageTitle: string; pont: string; cim: string; leiras: string }) {
    return this.request(`/staging/${stagingId}/items`, {
      method: 'POST',
      body: JSON.stringify(item),
    });
  }

  /**
   * A véglegesítés szakaszos: egy hívás ~12 mp-ig dolgozik, majd { done:false, processed, total } választ ad;
   * a hívó addig ismétli, amíg done:true nem jön (lásd applyStagingBatchAll).
   */
  static async applyStagingBatch(stagingId: string, mode: 'APPEND' | 'UPSERT' | 'REPLACE') {
    return this.request<{ appliedRecords: number; appliedFiles?: number; warnings?: string[] }>(`/staging/${stagingId}/apply`, {
      method: 'POST',
      body: JSON.stringify({ mode }),
      timeout: 120000,
    }) as Promise<{ ok: boolean; error?: string; busy?: boolean; done?: boolean; processed?: number; total?: number; appliedRecords?: number; appliedFiles?: number; warnings?: string[] }>;
  }

  /** a teljes köteg véglegesítése szakaszonként, haladásjelzéssel; hálózati hiba / időtúllépés után folytat */
  static async applyStagingBatchAll(stagingId: string, mode: 'APPEND' | 'UPSERT' | 'REPLACE', onProgress?: (processed: number, total: number, note?: string) => void) {
    let fails = 0;
    let busyWaits = 0;
    for (let round = 0; round < 3000; round++) {
      const res = await this.applyStagingBatch(stagingId, mode);
      if (res.ok) {
        fails = 0; busyWaits = 0;
        onProgress?.(res.processed || 0, res.total || 0);
        if (res.done !== false) return res;
        continue;
      }
      // zárolva: egy megszakadt szakasz zárolása 60 mp tétlenség után feloldódik → türelmesen kivárjuk (max. ~5 perc)
      if (res.busy) {
        if (++busyWaits > 40) return res;
        onProgress?.(-1, -1, 'Az előző szakasz lezárására várunk…');
        await new Promise((r) => setTimeout(r, 8000));
        continue;
      }
      // átmeneti hiba (időtúllépés, hálózat, szerverhiba): rövid várakozás után folytatjuk
      if (/időtúllépés|hálózat|network|fetch|Belső szerverhiba|Szerver hiba/i.test(res.error || '')) {
        if (++fails > 12) return res;
        await new Promise((r) => setTimeout(r, Math.min(15000, 2000 * fails)));
        continue;
      }
      return res;
    }
    return { ok: false, error: 'A véglegesítés nem fejeződött be' } as { ok: boolean; error?: string; appliedRecords?: number; appliedFiles?: number; warnings?: string[] };
  }

  // ── SYNC (Multi-Cloud & Helyi Replikáció) ────────────────────────
  static async getSyncStatus() {
    return this.request<SyncStatus>('/sync/status');
  }

  static async pullSync(sinceId: number = 0) {
    return this.request('/sync/pull', {
      method: 'POST',
      body: JSON.stringify({ since_id: sinceId }),
    });
  }

  static async pushSync(events: any[]) {
    return this.request('/sync/push', {
      method: 'POST',
      body: JSON.stringify({ events }),
    });
  }

  static async exportSyncPackage(tenantId: number, sinceVersion: number = 0, sinceTimestamp?: string) {
    return this.request<{
      ok: boolean;
      tenant_id: number;
      exported_at: string;
      package: {
        tenant: any;
        pages: any[];
        fields: any[];
        records: any[];
        attachments: any[];
      };
      error?: string;
    }>('/sync/export-package', {
      method: 'POST',
      body: JSON.stringify({
        tenant_id: tenantId,
        since_version: sinceVersion,
        since_timestamp: sinceTimestamp,
      }),
    });
  }

  static async applySyncPackage(pkg: any) {
    return this.request<{
      ok: boolean;
      tenant_id: number;
      tenant_slug: string;
      applied: {
        pages: number;
        records: number;
        attachments: number;
      };
      missing_files: Array<{
        r2_key: string;
        file_name: string;
        mime_type: string;
        file_size: number;
      }>;
      error?: string;
    }>('/sync/apply-package', {
      method: 'POST',
      body: JSON.stringify({ package: pkg }),
    });
  }

  static async remoteRequest<T = any>(
    remoteUrl: string,
    path: string,
    token?: string | null,
    options: RequestInit = {}
  ): Promise<{ ok: boolean; data?: T; error?: string; [key: string]: any }> {
    const cleanBase = remoteUrl.replace(/\/+$/, '');
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    const url = `${cleanBase}${cleanPath.startsWith('/api') ? cleanPath : `/api${cleanPath}`}`;

    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string> || {}),
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    if (!(options.body instanceof FormData) && !(options.body instanceof ArrayBuffer) && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }

    try {
      const res = await fetch(url, { ...options, headers });
      const json = await res.json().catch(() => null);
      if (!res.ok) {
        return {
          ok: false,
          error: json?.error || `Hálózati hiba (${res.status}: ${res.statusText})`,
          status: res.status,
        };
      }
      return json || { ok: true };
    } catch (e: any) {
      return { ok: false, error: e.message || 'Nem érhető el a távoli szerver' };
    }
  }

  static async loginRemote(remoteUrl: string, email: string, password: string): Promise<{ ok: boolean; token?: string; error?: string }> {
    const res = await this.remoteRequest(remoteUrl, '/auth/login', null, {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    });
    if (res.ok && res.token) {
      return { ok: true, token: res.token };
    }
    return { ok: false, error: res.error || 'Sikertelen bejelentkezés a távoli szerveren' };
  }

  static async transferSyncFile(
    sourceBaseUrl: string,
    sourceToken: string | null,
    targetBaseUrl: string,
    targetToken: string | null,
    r2Key: string,
    contentType: string = 'application/octet-stream'
  ): Promise<{ ok: boolean; error?: string }> {
    try {
      // 1. Letöltés a forrásból
      const cleanSrc = sourceBaseUrl.replace(/\/+$/, '');
      const srcUrl = `${cleanSrc}/api/sync/file/${encodeURIComponent(r2Key)}`;
      const srcHeaders: Record<string, string> = {};
      if (sourceToken) srcHeaders['Authorization'] = `Bearer ${sourceToken}`;

      const getRes = await fetch(srcUrl, { headers: srcHeaders });
      if (!getRes.ok) {
        return { ok: false, error: `Nem sikerült letölteni a fájlt a forrásból: ${r2Key} (${getRes.status})` };
      }

      const fileBuffer = await getRes.arrayBuffer();

      // 2. Feltöltés a célra
      const cleanTarget = targetBaseUrl.replace(/\/+$/, '');
      const targetUrl = `${cleanTarget}/api/sync/file`;
      const targetHeaders: Record<string, string> = {
        'x-r2-key': encodeURIComponent(r2Key),
        'Content-Type': contentType,
      };
      if (targetToken) targetHeaders['Authorization'] = `Bearer ${targetToken}`;

      const putRes = await fetch(targetUrl, {
        method: 'POST',
        headers: targetHeaders,
        body: fileBuffer,
      });

      if (!putRes.ok) {
        const errJson = await putRes.json().catch(() => null);
        return { ok: false, error: errJson?.error || `Feltöltési hiba a cél szerveren (${putRes.status})` };
      }

      return { ok: true };
    } catch (e: any) {
      return { ok: false, error: e.message || 'Fájlátviteli hiba' };
    }
  }

  // ── PERSONNEL ───────────────────────────────────────────────────
  /** @param entityId kapcsolt intézmény belső szervezete (v61); nélküle az anyaszervezeté */
  static async getPersonnelOverview(tenantId?: number, entityId?: number | null) {
    const q = new URLSearchParams();
    if (tenantId) q.set('tenant_id', String(tenantId));
    if (entityId) q.set('entity_id', String(entityId));
    const qs = q.toString() ? `?${q}` : '';
    return this.request<{
      org_units: OrgUnit[];
      positions: Position[];
      persons: Person[];
      current_assignments?: { person_id: number; position_id: number }[];
      chart_settings?: OrgChartSettings;
      /** false: a v60-as séma még nincs lefuttatva (hierarchia nem menthető) */
      hierarchy_ready?: boolean;
      /** false: a v61-es séma még nincs lefuttatva (kapcsolt intézmény belső szervezete) */
      entity_ready?: boolean;
    }>(`/personnel/overview${qs}`);
  }

  static async getOrgChartSettings(tenantId?: number, entityId?: number | null) {
    const q = new URLSearchParams();
    if (tenantId) q.set('tenant_id', String(tenantId));
    if (entityId) q.set('entity_id', String(entityId));
    return this.request<OrgChartSettings>(`/org-chart/settings${q.toString() ? `?${q}` : ''}`);
  }

  /** @param entityId kapcsolt intézmény (v61); nélküle az anyaszervezet diagramja */
  static async saveOrgChartSettings(settings: OrgChartSettings, tenantId?: number, entityId?: number | null) {
    return this.request<OrgChartSettings>('/org-chart/settings', {
      method: 'PUT',
      body: JSON.stringify({ ...settings, ...(tenantId ? { tenant_id: tenantId } : {}), ...(entityId ? { entity_id: entityId } : {}) }),
    });
  }

  static async getLinkedSettings(tenantId?: number) {
    return this.request<LinkedSettings>(`/linked-entities/settings${tenantId ? `?tenant_id=${tenantId}` : ''}`);
  }

  static async saveLinkedSettings(settings: Partial<LinkedSettings>, tenantId?: number) {
    return this.request<LinkedSettings>('/linked-entities/settings', {
      method: 'PUT',
      body: JSON.stringify({ ...settings, ...(tenantId ? { tenant_id: tenantId } : {}) }),
    });
  }

  static async createOrgUnit(unit: Partial<OrgUnit>) {
    return this.request('/org-units', {
      method: 'POST',
      body: JSON.stringify(unit),
    });
  }

  static async updateOrgUnit(id: number, unit: Partial<OrgUnit>) {
    return this.request(`/org-units/${id}`, {
      method: 'PUT',
      body: JSON.stringify(unit),
    });
  }

  static async deleteOrgUnit(id: number) {
    return this.request(`/org-units/${id}`, {
      method: 'DELETE',
    });
  }

  static async createPosition(pos: Partial<Position>) {
    return this.request('/positions', {
      method: 'POST',
      body: JSON.stringify(pos),
    });
  }

  static async updatePosition(id: number, pos: Partial<Position>) {
    return this.request(`/positions/${id}`, {
      method: 'PUT',
      body: JSON.stringify(pos),
    });
  }

  static async deletePosition(id: number) {
    return this.request(`/positions/${id}`, {
      method: 'DELETE',
    });
  }

  static async createPerson(person: Partial<Person>) {
    return this.request('/persons', {
      method: 'POST',
      body: JSON.stringify(person),
    });
  }

  static async updatePerson(id: number, person: Partial<Person>) {
    return this.request(`/persons/${id}`, {
      method: 'PUT',
      body: JSON.stringify(person),
    });
  }

  static async deletePerson(id: number) {
    return this.request(`/persons/${id}`, {
      method: 'DELETE',
    });
  }

  static async assignPersonToPosition(posId: number, data: { person_id?: number; remove_person_id?: number; auto_replace?: boolean }) {
    return this.request(`/positions/${posId}/assign`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ── SECURITY & USERS ────────────────────────────────────────────
  // B7: Self-Hosted Példányok modul (GET /api/selfhost/instances – csak superadmin)
  static async getSelfhostInstances() {
    return this.request<Array<{ id: string; org_name?: string; version?: string; contact_email?: string; last_seen?: string; first_seen?: string }>>('/selfhost/instances');
  }

  static async getUsers(tenantId?: number) {
    const qs = tenantId ? `?tenant_id=${tenantId}` : '';
    return this.request<AdminUser[]>(`/users${qs}`);
  }

  static async createUser(userData: {
    name: string;
    email: string;
    notification_email?: string;
    password?: string;
    role: string;
    tenant_id?: number;
    show_in_demo?: number;
    demo_order?: number;
  }) {
    return this.request<{ id: number; message: string }>('/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  static async updateUser(
    id: number,
    userData: {
      name?: string;
      email?: string;
      notification_email?: string;
      password?: string;
      role?: string;
      active?: number;
      tenant_id?: number;
      show_in_demo?: number;
      demo_order?: number;
    }
  ) {
    return this.request<{ message: string }>(`/users/${id}`, {
      method: 'PUT',
      body: JSON.stringify(userData),
    });
  }

  static async toggleUserActive(id: number) {
    return this.request<{ active: number; message: string }>(`/users/${id}/toggle-active`, {
      method: 'PATCH',
      body: '{}',
    });
  }

  static async toggleUserDemo(id: number) {
    return this.request<{ show_in_demo: number; message: string }>(`/users/${id}/toggle-demo`, {
      method: 'PATCH',
      body: '{}',
    });
  }

  static async deleteUser(id: number) {
    return this.request<{ message: string }>(`/users/${id}`, {
      method: 'DELETE',
    });
  }

  static async resetUser2FA(id: number) {
    return this.request<{ message: string }>(`/users/${id}/reset-2fa`, {
      method: 'POST',
      body: '{}',
    });
  }

  static async getAuditLogs(params?: { limit?: number; action?: string; tenant_id?: number } | number) {
    const limit = typeof params === 'number' ? params : (params?.limit || 100);
    const q = new URLSearchParams();
    q.set('limit', String(limit));
    if (typeof params === 'object') {
      if (params?.action) q.set('action', params.action);
      if (params?.tenant_id) q.set('tenant_id', String(params.tenant_id));
    }
    return this.request<AuditLogEntry[]>(`/audit?${q.toString()}`);
  }

  // ── Adatbázis-séma és teljes mentés (superadmin) ──
  static async getSchemaStatus() {
    return this.request<never>('/system/schema/status') as Promise<{
      ok: boolean; error?: string; expected_version: number; up_to_date: boolean; plan_hash: string; backup_storage: boolean; backup_mode?: 'cloudflare-export' | 'worker-dump';
      steps: { kind: string; table: string; column?: string; index?: string; sql: string; label: string; notes?: string[] }[];
      extra_tables: string[]; extra_columns: string[];
    }>;
  }

  static async applySchemaUpdate(planHash: string) {
    return this.request<never>('/system/schema/apply', { method: 'POST', body: JSON.stringify({ plan_hash: planHash }) }) as Promise<{
      ok: boolean; error?: string; plan_changed?: boolean; nothing_to_do?: boolean; run_id?: string;
      backup?: { key: string; tables: number | null; rows: number | null; bytes: number; format?: string; warning?: string };
      results?: { label: string; ok: boolean; error?: string }[]; failed?: { label: string; error: string } | null; remaining?: number;
    }>;
  }

  static async createFullBackup() {
    return this.request<never>('/system/schema/backup', { method: 'POST' }) as Promise<{ ok: boolean; error?: string; backup?: { key: string; tables: number | null; rows: number | null; bytes: number; format?: string; warning?: string } }>;
  }

  static async listFullBackups() {
    return this.request<{ key: string; size: number; uploaded: string }[]>('/system/schema/backups');
  }

  static async getSchemaHistory() {
    return this.request<{ run_id: string; created_at: string; backup_key: string | null; steps: number; ok_steps: number; error: string | null }[]>('/system/schema/history');
  }

  /** Teljes mentés letöltése: tárolt mentés (key) vagy azonnal készített (key nélkül). */
  static async downloadFullBackup(key?: string): Promise<{ blob: Blob; filename: string }> {
    const token = this.getToken();
    const path = key ? `/system/schema/backup?key=${encodeURIComponent(key)}` : '/system/schema/backup-now';
    const res = await fetch(`${API_BASE}${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) {
      let msg = `HTTP ${res.status}`;
      try { msg = (await res.json()).error || msg; } catch { /* nem JSON */ }
      throw new Error(msg);
    }
    const cd = res.headers.get('Content-Disposition') || '';
    const m = cd.match(/filename="([^"]+)"/);
    return { blob: await res.blob(), filename: m ? m[1] : 'kozad_teljes_mentes.sql' };
  }

  static async exportAuditXLSX(params?: { action?: string; tenant_id?: number; search?: string }): Promise<Blob> {
    const token = this.getToken();
    const res = await fetch(`${API_BASE}/audit/export/xlsx`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(params || {}),
    });
    if (!res.ok) throw new Error('Audit napló XLSX export hiba');
    return res.blob();
  }

  static async getActivityAnalytics(params?: { days?: number | string; tenant_id?: number | string }) {
    const q = new URLSearchParams();
    if (params?.days !== undefined && params?.days !== 'all') q.set('days', String(params.days));
    if (params?.tenant_id !== undefined && params?.tenant_id !== 'all') q.set('tenant_id', String(params.tenant_id));
    const qs = q.toString() ? `?${q.toString()}` : '';
    return this.request<import('../types').ActivityAnalyticsData>(`/admin/analytics/activity${qs}`);
  }

  static async trackActivity(action: string, detail?: any) {
    try {
      return await this.request<{ ok: boolean }>('/admin/analytics/track', {
        method: 'POST',
        body: JSON.stringify({ action, detail }),
      });
    } catch {
      return { ok: false };
    }
  }

  // ── BRANDING & SETTINGS ─────────────────────────────────────────
  static async getBranding() {
    return this.request<Tenant>('/branding');
  }

  static async getSystemBranding() {
    return this.request<SystemBrandingConfig>('/system-branding');
  }

  static async updateSystemBranding(data: Partial<SystemBrandingConfig>) {
    return this.request<SystemBrandingConfig>('/system-branding', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  static async updateSettings(settings: Record<string, any>) {
    return this.request('/settings', {
      method: 'POST',
      body: JSON.stringify(settings),
    });
  }

  // ── TENANTS ─────────────────────────────────────────────────────
  static async getTenants() {
    return this.request<Tenant[]>('/tenants');
  }

  static async createTenant(tenantData: Partial<Tenant> & { provision_template?: boolean }) {
    return this.request('/tenants', {
      method: 'POST',
      body: JSON.stringify(tenantData),
      timeout: tenantData.provision_template ? 60000 : undefined,
    });
  }

  static async updateTenant(id: number, tenantData: Partial<Tenant>) {
    return this.request(`/tenants/${id}`, {
      method: 'PUT',
      body: JSON.stringify(tenantData),
    });
  }

  static async deleteTenant(id: number) {
    return this.request(`/tenants/${id}`, {
      method: 'DELETE',
    });
  }

  static async compareTenantsWithRemote(remoteUrl?: string, secretKey?: string, snapshotTenants?: Tenant[]) {
    return this.request<{
      ok: boolean;
      stats: {
        local_count: number;
        remote_count: number;
        local_only_count: number;
        remote_only_count: number;
        difference_count: number;
        identical_count: number;
        remote_url: string;
        checked_at: string;
      };
      local_only: Tenant[];
      remote_only: Tenant[];
      differences: Array<{
        slug: string;
        name: string;
        local: Tenant;
        remote: Tenant;
        diff_fields: Array<{
          field: string;
          label: string;
          local_val: any;
          remote_val: any;
        }>;
      }>;
      identical: Array<{
        slug: string;
        name: string;
        tenant: Tenant;
      }>;
      all_local: Tenant[];
      all_remote: Tenant[];
    }>('/admin/tenants/diff', {
      method: 'POST',
      body: JSON.stringify({
        // S14/S1: cél és kulcs a konfigurációból / a felhasználótól – nincs beégetett domain vagy kulcs.
        remote_url: remoteUrl || (typeof window !== 'undefined' && (window.location.hostname.includes('localhost') || window.location.hostname.includes('127.0.0.1')) ? (DEPLOYMENT.productionUrl || 'http://localhost:8788') : 'http://localhost:8788'),
        secret_key: secretKey || undefined,
        snapshot_tenants: snapshotTenants,
      }),
    });
  }

  static async pushLocalSnapshotToRemote(remoteUrl: string, secretKey: string, tenants: Tenant[]) {
    try {
      const url = remoteUrl.trim().replace(/\/+$/, '') + '/api/admin/tenants/push-local-snapshot';
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(secretKey ? { 'X-Sync-Key': secretKey } : {}),
        },
        body: JSON.stringify({
          tenants,
          source: 'local_push',
        }),
      });
      return await res.json();
    } catch (e: any) {
      return { ok: false, error: e.message || 'Nem sikerült az állapot beküldése' };
    }
  }

  static async cloneTenantToProduction(id: number, options?: { name?: string; slug?: string; copy_users?: boolean }) {
    return this.request<{ ok: boolean; id: number; name: string; slug: string; message: string }>(`/tenants/${id}/clone-to-production`, {
      method: 'POST',
      body: JSON.stringify(options || {}),
    });
  }

  static async promoteTenantToProduction(id: number) {
    return this.request<{ ok: boolean; id: number; name: string; message: string }>(`/tenants/${id}/promote-to-production`, {
      method: 'POST',
    });
  }

  static async exportTenantsXLSX(): Promise<Blob> {
    const token = this.getToken();
    const res = await fetch(`${API_BASE}/tenants/export/xlsx`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    if (!res.ok) throw new Error('Szervezetek XLSX export hiba');
    return res.blob();
  }

  // ── COMPLIANCE RIORTOK ──────────────────────────────────────────
  /** szigorú megfelelőségi kiértékelés; tenantId: csak egy szervezet (a saját nézethez) */
  static async getComplianceOverview(tenantId?: number) {
    return this.request<ComplianceOverviewItem[]>(`/compliance/overview${tenantId ? `?tenant_id=${tenantId}` : ''}`);
  }

  static async exportComplianceXLSX(): Promise<Blob> {
    const token = this.getToken();
    const res = await fetch(`${API_BASE}/compliance/export/xlsx`, {
      method: 'POST',
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    if (!res.ok) throw new Error('Megfelelőségi XLSX export hiba');
    return res.blob();
  }

  // ── WEBOLDAL SCRAPER ────────────────────────────────────────────
  static async scrapeDiscover(reqBody: { sourceUrls?: string[]; queue?: any[]; visited?: any[]; plan?: any }) {
    return this.request<any>('/admin/scrape/discover', {
      method: 'POST',
      body: JSON.stringify(reqBody),
      timeout: 60000,
    });
  }

  static async scrapeImportStart(plan: any, targetTenantId?: number, newTenant?: { name: string; slug: string }) {
    return this.request<{ ok: boolean; import_id: number; progress_total: number; target_tenant_id: number }>('/admin/scrape/import-start', {
      method: 'POST',
      body: JSON.stringify({ plan, target_tenant_id: targetTenantId, new_tenant: newTenant }),
      timeout: 60000,
    });
  }

  static async scrapeImportStep(importId: number) {
    return this.request<{ ok: boolean; done: boolean; progress_done: number; progress_total: number; error?: string }>('/admin/scrape/import-step', {
      method: 'POST',
      body: JSON.stringify({ import_id: importId }),
      // egy lépés: legfeljebb ~40 mp + egy melléklet letöltése (a beállított időkorlát, max. 300 mp)
      timeout: 420000,
    });
  }

  static async getScrapeImportStatus(importId: number) {
    return this.request<any>(`/admin/scrape/import/${importId}`, { timeout: 30000 });
  }

  // ── PUBLIKUS PORTÁL ─────────────────────────────────────────────
  static async getPublicTenant(slug: string) {
    return this.request<{
      tenant: any;
      pages: (PageItem & { description?: string; icon?: string })[];
      available_years?: number[];
    }>(`/public/${encodeURIComponent(slug)}`);
  }

  static async getPublicPage(tenantSlug: string, pageSlug: string, year?: string | number, sortDir?: string) {
    const params = new URLSearchParams();
    if (year !== undefined && year !== null && year !== '') params.append('year', String(year));
    if (sortDir) params.append('sort_dir', sortDir);
    const query = params.toString() ? `?${params.toString()}` : '';
    return this.request<{
      tenant: any;
      page: PageItem;
      fields: any[];
      records: any[];
      available_years?: number[];
    }>(`/public/${encodeURIComponent(tenantSlug)}/${encodeURIComponent(pageSlug)}${query}`);
  }

  // ── USAGE & SYSTEM MONITORING ──────────────────────────────────
  static async getUsage() {
    return this.request<StorageUsage>('/usage');
  }

  static async getSystemMonitoring() {
    return this.request<any>('/system/monitoring');
  }

  // Valódi Cloudflare-felhasználás (GraphQL Analytics); refresh=true: gyorsítótár nélkül
  static async getCloudflareUsage(refresh = false) {
    return this.request<any>(`/system/cf-usage${refresh ? '?refresh=1' : ''}`);
  }

  // ── KÖZPONTI JOGSZABÁLYGYŰJTEMÉNY ───────────────────────────────
  static async getRegulations(params: { search?: string; category?: string; tenant_id?: number; year?: string | number } = {}) {
    const q = new URLSearchParams();
    if (params.search) q.set('search', params.search);
    if (params.category) q.set('category', params.category);
    if (params.tenant_id) q.set('tenant_id', String(params.tenant_id));
    if (params.year !== undefined && params.year !== 'all') q.set('year', String(params.year));
    const qs = q.toString() ? `?${q.toString()}` : '';
    return this.request<LegalRegulation[]>(`/regulations${qs}`);
  }

  static async createRegulation(data: Partial<LegalRegulation>) {
    return this.request<{ id: number }>('/regulations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  static async updateRegulation(id: number, data: Partial<LegalRegulation>) {
    return this.request(`/regulations/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  static async deleteRegulation(id: number) {
    return this.request(`/regulations/${id}`, {
      method: 'DELETE',
    });
  }

  static async seedRegulations() {
    return this.request<{ inserted: number }>('/regulations/seed', {
      method: 'POST',
    });
  }

  static async getRecordRegulations(recordId: number) {
    return this.request<LegalRegulation[]>(`/records/${recordId}/regulations`);
  }

  static async setRecordRegulations(recordId: number, regulationIds: number[]) {
    return this.request(`/records/${recordId}/regulations`, {
      method: 'POST',
      body: JSON.stringify({ regulation_ids: regulationIds }),
    });
  }

  static async getRegulationReferences(regulationId: number) {
    return this.request<RegulationReferenceItem[]>(`/regulations/${regulationId}/references`);
  }

  static async addRegulationReference(regulationId: number, recordId: number, sectionNote?: string) {
    return this.request(`/regulations/${regulationId}/references`, {
      method: 'POST',
      body: JSON.stringify({ record_id: recordId, section_note: sectionNote }),
    });
  }

  static async removeRegulationReference(regulationId: number, recordId: number) {
    return this.request(`/regulations/${regulationId}/references/${recordId}`, {
      method: 'DELETE',
    });
  }

  static async getCandidateRecords() {
    return this.request<CandidateRecordItem[]>('/regulations/candidate-records');
  }

  // ── KAPCSOLT ENTITÁSOK (SZERVEK, INTÉZMÉNYEK, CÉGEK) ───────────
  static async getLinkedEntities(params: { type?: string; tenant_id?: number } = {}) {
    const q = new URLSearchParams();
    if (params.type) q.set('type', params.type);
    if (params.tenant_id) q.set('tenant_id', String(params.tenant_id));
    const qs = q.toString() ? `?${q.toString()}` : '';
    return this.request<LinkedEntity[]>(`/linked-entities${qs}`);
  }

  static async createLinkedEntity(data: Partial<LinkedEntity>) {
    return this.request<{ id: number }>('/linked-entities', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  static async updateLinkedEntity(id: number, data: Partial<LinkedEntity>) {
    return this.request(`/linked-entities/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  static async deleteLinkedEntity(id: number) {
    return this.request(`/linked-entities/${id}`, {
      method: 'DELETE',
    });
  }

  static async getLinkedEntityReferences(id: number) {
    return this.request<{
      entity_id: number;
      name: string;
      usage_count: number;
      data: { record_id: number; page_id: number; page_title: string; pont: string; megnevezes: string }[];
    }>(`/linked-entities/${id}/references`);
  }

  // ── KÖZZÉTÉTELI SABLON ÉS VÁLTOZÓ MOTOR ──────────────────────────
  static async getTemplateDefinition(tenantId?: number, version?: string) {
    const p = new URLSearchParams();
    if (tenantId) p.set('tenant_id', String(tenantId));
    if (version) p.set('version', version);
    const qs = p.toString() ? `?${p.toString()}` : '';
    return this.request<TemplateDefinition>(`/template/definition${qs}`);
  }

  /** a szervezet sablonváltozóinak mentése (csak a törzsadattól eltérő értékek tárolódnak) */
  static async saveTemplateVariables(tenantId: number | undefined, values: Record<string, string>, custom?: { key: string; label: string }[], opts?: { merge?: boolean; addCustom?: { key: string; label: string }[] }) {
    return this.request<{
      suggestedVariables: Record<string, string>;
      savedKeys: string[];
      customVariables: { key: string; label: string }[];
      variableDefinitions: TemplateVariableDefinition[];
      ignored?: string[];
    }>('/template/variables', {
      method: 'PUT',
      body: JSON.stringify({ tenant_id: tenantId, values, ...(custom ? { custom } : {}), ...(opts?.merge ? { merge: true, add_custom: opts.addCustom || [] } : {}) }),
    });
  }

  static async provisionTemplate(params: {
    tenant_id?: number;
    variables: Record<string, string>;
    mode?: 'MERGE' | 'REPLACE' | 'MIGRATE';
    template_version?: string;
    dry_run?: boolean;
    custom_variables?: { key: string; label: string }[];
  }) {
    return this.request<{
      createdPagesCount: number;
      createdRecordsCount: number;
      linkedRegulationsCount: number;
      renumbered?: number;
    }>('/template/provision', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  /** batch mód: szervezetenként a 2026-os lista állapota (csak superadmin) */
  /** felhasználónkénti megjelenítési beállítások (böngészőtől független) */
  static async getUiPrefs() {
    return this.request<Record<string, unknown>>('/me/ui-prefs');
  }

  static async saveUiPrefs(prefs: Record<string, unknown>) {
    return this.request<Record<string, unknown>>('/me/ui-prefs', { method: 'PUT', body: JSON.stringify(prefs) });
  }

  /** a 2026-os lista egy főpontja a szervezet adataival kitöltve + a már meglévő bejegyzések erre a pontra */
  static async getTemplatePoint(pont: string) {
    return this.request<never>(`/template/point?pont=${encodeURIComponent(pont)}`) as Promise<TemplatePointInfo & { error?: string }>;
  }

  // ── Tartalom-változók (egy bejegyzés teljes tartalma újrahasznosítható blokként) ──
  static async getContentSnippets(tenantId?: number) {
    return this.request<ContentSnippet[]>(`/content-snippets${tenantId ? `?tenant_id=${tenantId}` : ''}`) as Promise<{ ok: boolean; data?: ContentSnippet[]; error?: string }>;
  }

  static async prepareContentSnippet(params: { tenant_id?: number; text: string; html?: string }) {
    return this.request<never>('/content-snippets/prepare', { method: 'POST', body: JSON.stringify(params) }) as Promise<{ ok: boolean; error?: string; text?: string; html?: string; replaced?: { key: string; count: number }[]; source_tenant?: { id: number; name: string } }>;
  }

  static async createContentSnippet(params: { name: string; scope: 'global' | 'tenant'; tenant_id?: number; text: string; html?: string }) {
    return this.request<never>('/content-snippets', { method: 'POST', body: JSON.stringify(params) }) as Promise<{ ok: boolean; id?: string; error?: string }>;
  }

  static async updateContentSnippet(id: string, params: { name?: string; text?: string; html?: string }) {
    return this.request(`/content-snippets/${id}`, { method: 'PUT', body: JSON.stringify(params) });
  }

  static async deleteContentSnippet(id: string) {
    return this.request(`/content-snippets/${id}`, { method: 'DELETE' });
  }

  static async getTemplateEditor() {
    return this.request<never>('/admin/template/editor', { timeout: 30000 }) as Promise<TemplateEditorResponse>;
  }

  static async saveTemplateEditor(chapters: TemplateChapter[], source: 'editor' | 'xlsx' = 'editor') {
    return this.request<never>('/admin/template/editor', { method: 'PUT', body: JSON.stringify({ chapters, source }), timeout: 30000 }) as Promise<TemplateEditorResponse>;
  }

  static async resetTemplateEditor() {
    return this.request<never>('/admin/template/editor', { method: 'DELETE', timeout: 30000 }) as Promise<TemplateEditorResponse>;
  }

  static async getTemplateStatus() {
    return this.request<TemplateStatusRow[]>('/admin/template/status');
  }

  static async batchProvisionTemplates(params: { tenant_ids?: number[] | string; mode?: 'MERGE' | 'REPLACE' | 'MIGRATE' | 'TITLES' | 'SNIPPETS'; template_version?: string; dry_run?: boolean }) {
    return this.request<{
      ok: boolean;
      message: string;
      results: any[];
      totalCreatedPages: number;
      totalCreatedRecords: number;
      totalLinkedRegulations: number;
      errors?: { tenantId: number; error: string }[];
    }>('/admin/template/batch-provision', {
      method: 'POST',
      body: JSON.stringify(params),
      timeout: 120000,
    });
  }

  static async resetDatabase(params: {
    confirm_phrase: string;
    seed_demo_2026?: boolean;
    seed_regulations?: boolean;
    provision_tenant_ids?: number[] | string;
  }) {
    return this.request<{
      ok: boolean;
      message: string;
      preservedSuperadmins: any[];
      currentTenants: any[];
      seededOrgsCount: number;
      seededUsersCount: number;
      seededRegsCount: number;
      provisionedTenantsCount: number;
      provisionResults: any[];
    }>('/admin/database/reset', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  // ── SZÖVEG CSERE (KERESÉS & CSERE) ──────────────────────────────
  static async replacePageRecords(
    pageId: number,
    data: {
      pairs: ReplacePair[];
      caseSensitive?: boolean;
      useRegex?: boolean;
      preview?: boolean;
      scope?: 'all' | 'exact_year' | 'future_years';
      targetYear?: number | null;
    }
  ) {
    return this.request<ReplacePreviewResult>(`/pages/${pageId}/records/replace`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ── ÉV MŰVELETEK & ROLLOVER ──────────────────────────────────────
  static async rolloverYear(sourceYear: number | null, targetYear: number, tenantId?: number) {
    return this.request<{ ok: boolean; copied: number; target_year: number }>('/admin/years/rollover', {
      method: 'POST',
      body: JSON.stringify({ source_year: sourceYear, target_year: targetYear, tenant_id: tenantId }),
    });
  }

  // ── SZERVEZET DEMO SEMAFOR ───────────────────────────────────────
  static async toggleTenantDemo(tenantId: number, isDemo: boolean) {
    return this.request<{ ok: boolean }>(`/tenants/${tenantId}`, {
      method: 'PUT',
      body: JSON.stringify({ is_demo: isDemo ? 1 : 0 }),
    });
  }

  // ── FÁJLFELTÖLTÉSI SZABÁLYOK & BEÁLLÍTÁSOK ───────────────────────
  static async getUploadPolicy() {
    return this.request<UploadPolicy>('/settings');
  }

  static async saveUploadPolicy(policy: {
    upload_max_size_mb?: string;
    upload_allowed_ext?: string;
    upload_denied_ext_extra?: string;
  }) {
    return this.request<{ ok: boolean }>('/settings', {
      method: 'POST',
      body: JSON.stringify(policy),
    });
  }

  // ── 5 ÉVES DEMO CSOMAG LETÖLTÉS & HOMOKOZÓ (SANDBOX) MÓD ─────────
  static downloadDemoPackageZip() {
    const link = document.createElement('a');
    link.href = `${API_BASE}/public/demo-package/download-zip`;
    link.setAttribute('download', 'kozad_5eves_4intezmenyes_demo_csomag.zip');
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  // ── MAPPASTRUKTÚRA SABLON LETÖLTÉS (ZIP) ─────────────────────────
  /** mintafájlos mappasablon (mappasablon_2026_v2) beállítása */
  static async getFolderTemplateV2() {
    return this.request<{ configured: boolean; label: string; updated_at: string | null; source_tenant_id?: number | null; stats: { tenant_name: string; points: number; files: number } | null; samples?: { files: number; points: number } }>('/folder-template/v2');
  }

  // ── Központi mintadokumentum-tár (superadmin) ──
  static async getTemplateSamples() {
    return this.request<{ items: TemplateSampleItem[]; points: { pont: string; label: string; chapter: string }[] }>('/folder-template/samples');
  }
  static async uploadTemplateSamples(files: File[], pont?: string) {
    const fd = new FormData();
    files.forEach((f) => fd.append('files', f));
    if (pont) fd.append('pont', pont);
    return this.request<never>('/folder-template/samples', { method: 'POST', body: fd, timeout: 300000 }) as Promise<{ ok: boolean; error?: string; added?: { id: string; pont: string; filename: string }[]; skipped?: { name: string; reason: string }[]; total?: number }>;
  }
  static async copyTemplateSamplesFromTenant(tenantId: number) {
    return this.request<never>('/folder-template/samples/copy-tenant', { method: 'POST', body: JSON.stringify({ tenant_id: tenantId }), timeout: 300000 }) as Promise<{ ok: boolean; error?: string; added?: number; skipped?: { name: string; reason: string }[]; total?: number; source?: string }>;
  }
  static async moveTemplateSample(id: string, pont: string) {
    return this.request<never>(`/folder-template/samples/${id}`, { method: 'PATCH', body: JSON.stringify({ pont }) });
  }
  static async deleteTemplateSample(id: string | 'all') {
    return this.request<never>(id === 'all' ? '/folder-template/samples?all=1' : `/folder-template/samples/${id}`, { method: 'DELETE' });
  }

  static async setFolderTemplateV2(sourceTenantId: number | null, label = 'mappasablon_2026_v2') {
    return this.request<any>('/folder-template/v2', { method: 'PUT', body: JSON.stringify({ source_tenant_id: sourceTenantId, label }) });
  }

  static async downloadFolderTemplateZip(options: { blank?: boolean; year?: string; tenantId?: number; templateVersion?: string; variant?: 'v2' } = {}) {
    const token = this.getToken();
    const res = await fetch(`${API_BASE}/export/folder-template`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      // a szerver tenant_id / template_version néven várja
      body: JSON.stringify({ ...options, tenant_id: options.tenantId, template_version: options.templateVersion }),
    });
    if (!res.ok) {
      let msg = 'Nem sikerült letölteni a mappastruktúra sablont';
      try { msg = (await res.json()).error || msg; } catch { /* */ }
      throw new Error(msg);
    }
    const cd = res.headers.get('Content-Disposition') || '';
    const fn = cd.match(/filename="([^"]+)"/)?.[1];
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = options.variant === 'v2' && fn ? fn : options.blank ? 'kozzeteteli_mappastruktura_ures_sablon.zip' : 'kozzeteteli_mappastruktura.zip';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  }

  // ── KÖZADAT.HU CSV EXPORT (WINDOWS-1250) ─────────────────────────
  static async downloadKozadatCsv(options: { tenantId?: number; year?: number } = {}): Promise<{ ok: boolean; row_count: number; skipped_no_attachment: number }> {
    const token = this.getToken();
    const res = await fetch(`${API_BASE}/export/kozadat-csv`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({
        client_origin: typeof window !== 'undefined' ? window.location.origin : '',
        ...options,
      }),
    });

    if (!res.ok) {
      const errJson = await res.json().catch(() => null);
      throw new Error(errJson?.error || `Export hiba (${res.status})`);
    }

    const contentType = res.headers.get('content-type') || '';
    let rowCount = 0;
    let skippedCount = 0;
    let filename = `kozadat_export_${new Date().toISOString().slice(0, 10)}.csv`;

    if (contentType.includes('application/json')) {
      const r = await res.json();
      if (!r.ok) throw new Error(r.error || 'Export sikertelen');
      filename = r.filename || filename;
      rowCount = r.row_count ?? 0;
      skippedCount = r.skipped_no_attachment ?? 0;

      // Base64 dekódolása Uint8Array bájtsorozattá a helyes Windows-1250 kódolás megőrzéséhez
      const binary = atob(r.csv_base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: 'text/csv;charset=windows-1250' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } else {
      const disposition = res.headers.get('content-disposition');
      if (disposition && disposition.includes('filename=')) {
        const match = disposition.match(/filename="?([^";]+)"?/);
        if (match) filename = match[1];
      }
      rowCount = parseInt(res.headers.get('x-row-count') || '0', 10);
      skippedCount = parseInt(res.headers.get('x-skipped-count') || '0', 10);

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    }

    return { ok: true, row_count: rowCount, skipped_no_attachment: skippedCount };
  }

  static async enterSandbox(tenantId?: number) {
    return this.request<{
      ok: boolean;
      token: string;
      user: User;
      tenant: Tenant;
      original_tenant_id: number;
      is_sandbox: boolean;
    }>('/tenant/sandbox/enter', {
      method: 'POST',
      body: JSON.stringify({ tenant_id: tenantId }),
    });
  }

  static async resetSandbox(originalTenantId?: number) {
    return this.request<{ ok: boolean; message?: string }>('/tenant/sandbox/reset', {
      method: 'POST',
      body: JSON.stringify({ original_tenant_id: originalTenantId }),
    });
  }

  static async promoteSandbox(targetTenantId?: number, recordIds?: number[]) {
    return this.request<{ ok: boolean; message?: string; promoted_count?: number }>('/tenant/sandbox/promote', {
      method: 'POST',
      body: JSON.stringify({ target_tenant_id: targetTenantId, record_ids: recordIds }),
    });
  }

  // ── HIBAJEGYEK (TICKETS) ──────────────────────────────────────────
  static async getTickets(params?: { status?: string; priority?: string }) {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.priority) qs.set('priority', params.priority);
    const qStr = qs.toString() ? `?${qs.toString()}` : '';
    return this.request<any[]>(`/tickets${qStr}`);
  }

  static async uploadTicketAttachment(file: File | Blob, filename?: string) {
    const formData = new FormData();
    if (file instanceof File) {
      formData.append('file', file);
    } else {
      formData.append('file', file, filename || 'kepernyokep.png');
    }
    return this.request<{ name: string; key: string; size: number; type: string; url: string }>('/tickets/upload', {
      method: 'POST',
      body: formData,
    });
  }

  static async createTicket(data: { title: string; description: string; category: string; priority: string; module_context?: string; attachments?: any[] }) {
    return this.request<{ id: number; ticket_number: string; message: string }>('/tickets', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  static async getTicket(id: number) {
    return this.request<any>(`/tickets/${id}`);
  }

  static async updateTicket(id: number, data: { status?: string; priority?: string; title?: string; description?: string; assigned_to?: number; attachments?: any[] }) {
    return this.request<{ message: string }>(`/tickets/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  static async deleteTicket(id: number) {
    return this.request<{ message: string }>(`/tickets/${id}`, {
      method: 'DELETE',
    });
  }

  static async addTicketComment(id: number, comment: string, attachments?: any[]) {
    return this.request<{ message: string }>(`/tickets/${id}/comments`, {
      method: 'POST',
      body: JSON.stringify({ comment, attachments }),
    });
  }

  // ── SÚGÓ & TUDÁSBÁZIS (HELP CENTER) ──────────────────────────────
  static async getHelpArticles(q?: string, category?: string) {
    const qs = new URLSearchParams();
    if (q) qs.set('q', q);
    if (category) qs.set('category', category);
    const qStr = qs.toString() ? `?${qs.toString()}` : '';
    return this.request<any[]>(`/help/articles${qStr}`);
  }

  static async uploadHelpAttachment(file: File | Blob, filename?: string) {
    const formData = new FormData();
    if (file instanceof File) {
      formData.append('file', file);
    } else {
      formData.append('file', file, filename || 'sugo_fajl.png');
    }
    return this.request<{ name: string; key: string; size: number; type: string; url: string }>('/help/upload', {
      method: 'POST',
      body: formData,
    });
  }

  static async createHelpArticle(data: { title: string; category: string; keywords?: string; content: string; sort_order?: number; attachments?: any[] }) {
    return this.request<{ id: number; message: string }>('/help/articles', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  static async updateHelpArticle(id: number, data: Partial<{ title: string; category: string; keywords: string; content: string; sort_order: number; attachments: any[] }>) {
    return this.request<{ message: string }>(`/help/articles/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  static async deleteHelpArticle(id: number) {
    return this.request<{ message: string }>(`/help/articles/${id}`, {
      method: 'DELETE',
    });
  }

  // ── DPO MEGFELELŐSÉGI SZABÁLYOK (COMPLIANCE RULES) ───────────────
  static async getComplianceRules(tenantId: number) {
    return this.request<any[]>(`/tenants/${tenantId}/compliance-rules`);
  }

  // ── INTÉZMÉNYI (HOZZÁFÉRÉSI) CSOPORTOK ─────────────────────────
  static async getAccessGroups(tenantId: number) {
    return this.request<any[]>(`/access-groups?tenant_id=${tenantId}`);
  }
  static async getAccessGroupCatalog(tenantId: number) {
    return this.request<any>(`/access-groups/catalog?tenant_id=${tenantId}`);
  }
  static async createAccessGroup(body: { tenant_id: number; name: string; description?: string; linked_entity_id?: number | null }) {
    return this.request<{ id: number }>(`/access-groups`, { method: 'POST', body: JSON.stringify(body) });
  }
  static async updateAccessGroup(id: number, body: Record<string, any>) {
    return this.request(`/access-groups/${id}`, { method: 'PUT', body: JSON.stringify(body) });
  }
  static async deleteAccessGroup(id: number) {
    return this.request(`/access-groups/${id}`, { method: 'DELETE' });
  }
  static async getMyAccessGroups() {
    return this.request<any[]>(`/my-access-groups`);
  }

  // replace=true: a teljes szabálykészlet cseréje (a kivett alpont-felülírások is törlődnek)
  static async saveComplianceRules(tenantId: number, rules: any[], replace = false) {
    return this.request<{ message: string }>(`/tenants/${tenantId}/compliance-rules`, {
      method: 'POST',
      body: JSON.stringify({ rules, replace }),
    });
  }

  // ── TÁRHELY EGÉSZSÉG (STORAGE HEALTH) ────────────────────────────
  static async getStorageHealth() {
    return this.request<{
      healthy: boolean;
      zeroByteCount: number;
      zeroByteFiles: any[];
      missingFilesCount: number;
      missingFiles: any[];
    }>('/system/storage-health');
  }

  static async cleanupStorageHealth() {
    return this.request<{ deleted: number }>('/system/storage-health/cleanup', {
      method: 'POST',
    });
  }

  // ── KÜLSŐ TÜKÖR & BACKUP (külső PHP/MySQL tárhely) ───────────────
  static async getMirrorStatus() {
    return this.request<{
      mirror_url: string;
      last_sync_at: string | null;
      last_sync_status: string;
      configured: boolean;
    }>('/admin/mirror/status');
  }

  static async triggerMirrorSync(params?: { mirror_url?: string; mirror_key?: string }) {
    return this.request<{
      message: string;
      duration_ms: number;
      output: string;
    }>('/admin/mirror/trigger', {
      method: 'POST',
      body: JSON.stringify(params || {}),
      timeout: 120000,
    });
  }
}

/** a 2026-os közzétételi lista állapota egy szervezetnél (batch mód) */
export interface TemplateStatusRow {
  id: number;
  slug: string;
  name: string;
  is_demo: boolean;
  active: boolean;
  pages: number;
  records: number;
  /** a 2026-os lista pontjaiból ennyi van meg */
  found: number;
  total: number;
  /** régi (2026 előtti) számozású pontok száma */
  legacy: number;
  status: 'none' | 'legacy' | 'partial' | 'full' | 'other';
  last_template_at: string | null;
}

/** a 2026-os közzétételi lista egy tétele (főpont vagy „2.4 › Csoport” al-tétel) */
export interface TemplateRecord {
  pont: string;
  cim: string;
  short?: string;
  folderPath?: string;
  leiras?: string;
  kulso_linkek?: string;
  frissites?: string;
  megorzesi_ido?: string;
  legacy?: unknown[];
}

export interface TemplateChapter {
  pageTitle: string;
  slug: string;
  records: TemplateRecord[];
}

export interface TemplateEditorResponse {
  ok: boolean;
  error?: string;
  errors?: string[];
  chapters?: TemplateChapter[];
  factory?: TemplateChapter[];
  overridden?: boolean;
  meta?: { updated_at: string; updated_by: string; vs_factory?: { changed: number; added: number; removed: number } } | null;
}

export interface TrashItem {
  id: number;
  kind: 'page' | 'record' | 'attachment';
  ref_id: number;
  page_id: number | null;
  record_id: number | null;
  title: string | null;
  detail: string | null;
  size_bytes: number;
  deleted_at: string;
  expires_at: string;
  deleted_by_name: string | null;
  page_title: string | null;
}

export type ComplianceCategory = 'dpo_attachment' | 'dpo_data' | 'fields' | 'placeholder' | 'empty';

export interface ComplianceOverviewItem {
  tenant_id: number;
  tenant_name: string;
  tenant_slug: string;
  short_name?: string;
  active: number;
  total_pages: number;
  total_records: number;
  /** bejegyzések + a 2026-os lista bejegyzés nélküli (fő)pontjai */
  total_expected: number;
  completed_records: number;
  percentage: number;
  missing_required_count: number;
  missing_items: Array<{
    id: number; page_id: number; page_title: string; pont: string; megnevezes: string;
    requires_attachment: number; missing_data?: number; attachment_count: number; categories?: ComplianceCategory[]; reason: string;
  }>;
  uncovered_points: Array<{ pont: string; cim: string; chapter: string; page_id: number | null }>;
  uses_template: boolean;
  counts: Record<ComplianceCategory | 'uncovered', number>;
  pages: Array<{ page_id: number; title: string; total: number; completed: number; missing: number; uncovered: number; expected: number; percentage: number }>;
}

export interface QuotaDefaults { max_storage_mb: number; max_records: number; warn_percent: number }

export interface QuotaRequest {
  status: 'pending' | 'approved' | 'rejected';
  additional_mb: number;
  message: string;
  user_name: string;
  created_at: string;
  limit_mb_at_request: number;
  used_bytes_at_request: number;
  ticket_number?: string | null;
  decided_at?: string;
  decided_by?: string;
  new_limit_mb?: number | null;
  reason?: string | null;
}

export type QuotaRequestRow = QuotaRequest & { tenant_id: number; tenant_name: string; tenant_slug: string; own_max_storage_mb: number | null };

export interface QuotaStatus {
  tenant_id: number;
  tenant_name: string;
  storage: { attachments: number; trash: number; archive: number; total: number; limit_mb: number; limit_bytes: number; percent: number | null; source: 'tenant' | 'global'; level: 'ok' | 'warn' | 'full' };
  records: { used: number; limit: number; percent: number | null; source: 'tenant' | 'global'; level: 'ok' | 'warn' | 'full' };
  warn_percent: number;
  level: 'ok' | 'warn' | 'full';
  request: QuotaRequest | null;
}

export type ArchiveKind = 'page' | 'record' | 'attachment';

export interface ArchiveItem {
  id: number;
  uid: string | null;
  kind: ArchiveKind | 'version';
  ref_id: number;
  page_id: number | null;
  record_id: number | null;
  title: string | null;
  page_title: string | null;
  detail: string | null;
  size_bytes: number;
  pont: string | null;
  retention_text: string | null;
  retention_years: number | null;
  retention_min: number;
  retention_basis: 'archive' | 'publish' | null;
  basis_date: string | null;
  retain_until: string | null;
  source: 'manual' | 'auto';
  note: string | null;
  is_public: number;
  published_at: string | null;
  archived_at: string;
  archived_by_name: string | null;
  page_exists: number | null;
}

export interface ArchiveSummary {
  count: number;
  bytes: number;
  locked: number;
  unknown: number;
  max: { id: number; title: string | null; pont: string | null; retain_until: string; years: number | null; min: boolean; text: string | null; basis: string | null } | null;
}

export interface ArchiveRetention {
  pont: string; text: string; years: number | null; min: boolean; basis: 'archive' | 'publish'; basis_date: string; retain_until: string | null; extended?: boolean;
}

export interface ArchivePreview {
  items: (ArchiveRetention & { record_id: number; page_id: number; page_title: string | null; title: string })[];
  groups: { text: string; years: number | null; min: boolean; basis: string; count: number; max_until: string | null; ponts: string[] }[];
  max: (ArchiveRetention & { title: string }) | null;
  unknown: number;
  count: number;
}

export interface PublicArchiveItem {
  id: number;
  kind: string;
  title: string | null;
  page_id: number | null;
  page_title: string | null;
  pont: string | null;
  archived_at: string;
  published_at: string | null;
  retention_text: string | null;
  retain_until: string | null;
  records: { id: number; year: number | null; data: Record<string, any>; attachments: { id: number; filename: string; mime_type?: string; size?: number; url: string }[] }[];
  attachments: { id: number; filename: string; mime_type?: string; size?: number; url: string }[];
}

export interface TemplatePointInfo {
  ok: boolean;
  found: boolean;
  point?: { pont: string; cim: string; leiras: string; leiras_html?: string; snippet_ids?: string[]; kulso_linkek: string; frissites: string; megorzesi_ido: string; short: string };
  chapter?: { slug: string; pageTitle: string };
  existing?: { id: number; page_id: number; page_slug: string; page_title: string; year: number | null; cim: string; has_html: boolean }[];
}

export interface ContentSnippet {
  id: string;
  name: string;
  scope: 'global' | 'tenant';
  has_html: boolean;
  /** a szervezet adataival kitöltve (beszúráshoz) */
  text: string;
  html: string;
  /** superadminnak: a tárolt, helyőrzős alak */
  raw_text?: string;
  raw_html?: string;
  created_at?: string;
}
